import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApi } from '@/api/ApiContext';
import type { FixedAssetReconciliation } from '@/api/handlers/resources/fixedAssets/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import type { LocaleCode } from '@/api/models';
import { FixedAssetQueryKeys } from './fixedAssetQueryKeys';

const STALE_TIME_MS = 60_000;

/**
 * The register-vs-ledger tie-out, `GET /ledger/fixed-assets/reconciliation`.
 *
 * **The server holds both sides and states the verdict.** The client no longer
 * reads the register's totals, looks their accounts up in the trial balance and
 * compares — it asks the one service that has both. That removes the failure the
 * old shape made possible: a client-side subtraction of two figures fetched at
 * different moments, which can report a difference that is only a race.
 *
 * Keyed by locale because `accountName` is resolved server-side (the server's
 * own default is `ka` regardless of the app's language), so switching language
 * refetches rather than re-resolving a map the client does not have.
 */
export const useReconciliationQuery = () => {
    const { apiClient } = useApi();
    const { i18n } = useTranslation();
    const locale: LocaleCode = i18n.language === 'ka' ? 'ka' : 'en';

    return useQuery<ApiResponse<FixedAssetReconciliation>>({
        queryKey: FixedAssetQueryKeys.reconciliation(locale),
        queryFn: () => apiClient.fixedAssets.getReconciliation(locale),
        staleTime: STALE_TIME_MS,
    });
};
